import React, { useState, useEffect, useCallback } from 'react';
import {
  getCbamExecutive, getCbamProducts, getCbamSuppliers,
  getCbamShipments, getCbamDigitalTwin, getCbamSupplierCompliance
} from '../api';
import CbamExecutive from './CbamExecutive';
import CbamProducts from './CbamProducts';
import CbamSuppliers from './CbamSuppliers';
import CbamShipments from './CbamShipments';
import CbamDigitalTwin from './CbamDigitalTwin';
import CbamSupplierCompliance from './CbamSupplierCompliance';
import { useTranslation } from '../i18n/LanguageContext';

const CBAM_TAB_IDS = ['executive', 'products', 'suppliers', 'shipments', 'twin', 'compliance'];

export default function CbamModule({ trackFeature }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('executive');
  const handleTabChange = (tabId) => {
    setTab(tabId);
    if (trackFeature) trackFeature('cbam', `cbam_tab_${tabId}`, tabId);
  };
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [exec, prod, supp, ship, twin, compliance] = await Promise.all([
        getCbamExecutive(),
        getCbamProducts(),
        getCbamSuppliers(),
        getCbamShipments(),
        getCbamDigitalTwin(),
        getCbamSupplierCompliance(),
      ]);
      setData({
        executive: exec.data,
        products: prod.data,
        suppliers: supp.data,
        shipments: ship.data,
        twin: twin.data,
        compliance: compliance.data,
      });
    } catch (err) {
      console.error('Failed to load CBAM data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="cbam-loading">
        <div className="h-12 bg-surface border border-border rounded-lg w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-28 bg-surface border border-border rounded-lg" />
          ))}
        </div>
        <div className="h-64 bg-surface border border-border rounded-lg" />
      </div>
    );
  }

  const renderTab = () => {
    switch (tab) {
      case 'executive':
        return <CbamExecutive data={data.executive} />;
      case 'products':
        return <CbamProducts data={data.products} />;
      case 'suppliers':
        return <CbamSuppliers data={data.suppliers} />;
      case 'shipments':
        return <CbamShipments data={data.shipments} />;
      case 'twin':
        return <CbamDigitalTwin data={data.twin} />;
      case 'compliance':
        return <CbamSupplierCompliance data={data.compliance} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-5" data-testid="cbam-module">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-text-primary">
          {t('cbam.title')}
        </h1>
        <p className="text-sm font-body text-text-secondary mt-1">
          {t('cbam.subtitle')}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 overflow-x-auto pb-1 bg-surface border border-border rounded-lg p-1" data-testid="cbam-tabs">
        {CBAM_TAB_IDS.map((id) => (
          <button
            key={id}
            data-testid={`cbam-tab-${id}`}
            onClick={() => handleTabChange(id)}
            className={`
              whitespace-nowrap px-4 py-2 rounded-md text-sm font-body font-medium transition-all duration-200
              ${tab === id
                ? 'bg-primary text-white shadow-sm'
                : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
              }
            `}
          >
            {t(`cbam.tabs.${id}`)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTab()}
    </div>
  );
}
